'use client';

import { createContext, useContext, useEffect, useState } from 'react';

export type TextSize = 'standard' | 'large' | 'extra-large';

const SCALE: Record<TextSize, number> = {
  'standard':    1,
  'large':       1.15,
  'extra-large': 1.3,
};

const STORAGE_KEY = 'garden-text-size';

interface TextSizeContextValue {
  textSize: TextSize;
  setTextSize: (size: TextSize) => void;
}

const TextSizeContext = createContext<TextSizeContextValue>({
  textSize: 'standard',
  setTextSize: () => {},
});

export function TextSizeProvider({ children }: { children: React.ReactNode }) {
  const [textSize, setTextSizeState] = useState<TextSize>('standard');

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as TextSize | null;
    if (stored && stored in SCALE) setTextSizeState(stored);
  }, []);

  useEffect(() => {
    // data-text-size drives CSS overrides in globals.css
    document.documentElement.dataset.textSize = textSize;
  }, [textSize]);

  function setTextSize(size: TextSize) {
    setTextSizeState(size);
    localStorage.setItem(STORAGE_KEY, size);
  }

  return (
    <TextSizeContext.Provider value={{ textSize, setTextSize }}>
      {children}
    </TextSizeContext.Provider>
  );
}

export function useTextSize() {
  return useContext(TextSizeContext);
}
